import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Database, UserCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { fileUrl } from "@/components/ImageUpload";
import { fmt, levelName } from "@/lib/ratings";
import api from "@/lib/api";

const inputCls = "rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const SKILLS = ["2.0", "2.5", "3.0", "3.5", "4.0", "4.5", "5.0+"];
const SORTS = [["official", "Official rating"], ["composite", "Composite rating"], ["assessment", "Assessment rating"], ["casual", "Casual Play rating"], ["name", "Name"]];

function Rating({ v, prov }) {
  return <span className="mono font-bold tabular-nums">{fmt(v)}{v != null && prov && <span className="text-[9px] ml-1" style={{ color: "#f59e0b" }}>P</span>}</span>;
}

export default function PlayerDatabase() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("official");
  const [skill, setSkill] = useState("");
  const [city, setCity] = useState("");
  const [verified, setVerified] = useState(false);
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    api.get("/ratings/directory", { params: { q, sort, skill, city, verified_only: verified } }).then(({ data }) => setRows(data)).catch(() => setRows(false));
  }, [q, sort, skill, city, verified]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Player Database" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-2"><Database className="w-6 h-6 text-primary-c" /><h1 className="font-display font-bold text-2xl">Player Database</h1>
          {Array.isArray(rows) && <span className="text-xs mono glass rounded-full px-2.5 py-0.5" style={{ color: "var(--text-2)" }} data-testid="pdb-count">{rows.length}</span>}</div>
        <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>Official = verified match results · Assessment = 50/30/20 verified feedback · Composite = 70% Official + 30% Assessment · Casual = Open Play ratings · 0.0–6.0+ scale. <span style={{ color: "#f59e0b" }}>P</span> = incomplete.</p>
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />
            <input data-testid="pdb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Player ID or club" className={`${inputCls} w-full pl-9`} style={{ color: "var(--text)" }} />
          </div>
          <input data-testid="pdb-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Location" className={inputCls} style={{ color: "var(--text)" }} />
          <select data-testid="pdb-skill" value={skill} onChange={(e) => setSkill(e.target.value)} className={inputCls} style={{ color: "var(--text)" }}>
            <option value="">Any skill</option>{SKILLS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="pdb-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={inputCls} style={{ color: "var(--text)" }}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
          </select>
          <button data-testid="pdb-verified" onClick={() => setVerified((v) => !v)} className="rounded-xl px-3 py-2.5 text-sm font-bold"
            style={verified ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--surface)", color: "var(--text-2)", border: "1px solid var(--border)" }}>Verified only</button>
        </div>
        {rows === null ? <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>
          : rows === false ? <p className="text-sm py-20 text-center" style={{ color: "var(--text-2)" }}>Could not load the Player Database.</p>
          : (
            <div className="rounded-2xl glow-border overflow-x-auto" style={{ background: "var(--surface)" }}>
              <table className="w-full text-sm" data-testid="pdb-table">
                <thead><tr className="text-[10px] uppercase tracking-wider text-left" style={{ color: "var(--text-2)" }}>
                  <th className="px-4 py-3">Player</th><th className="px-3">Player ID</th><th className="px-3">Official</th><th className="px-3">Assessment</th><th className="px-3">Composite</th><th className="px-3">Casual</th><th className="px-3">Level</th><th className="px-3">Club</th><th className="px-3">Location</th><th className="px-3">Skill</th>
                </tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={10} className="text-center py-10" style={{ color: "var(--text-2)" }} data-testid="pdb-empty">No players match your filters.</td></tr>}
                  {rows.map((r) => {
                    const photo = r.photo ? fileUrl(r.photo) : r.picture;
                    return (
                      <tr key={r.user_id} onClick={() => nav(`/players/${r.user_id}`)} className="border-t cursor-pointer hover:bg-black/5 transition-colors" style={{ borderColor: "var(--border)" }} data-testid={`pdb-row-${r.user_id}`}>
                        <td className="px-4 py-2.5"><div className="flex items-center gap-2.5">
                          {photo ? <img src={photo} alt="" className="w-8 h-8 rounded-full object-cover" /> : <UserCircle className="w-8 h-8 text-primary-c" />}
                          <span className="font-semibold">{r.name}</span><VerifiedBadge verified={r.verified} testid={`pdb-verified-${r.user_id}`} />
                        </div></td>
                        <td className="px-3 mono text-xs">{r.player_code}</td>
                        <td className="px-3"><Rating v={r.official} prov={r.official_provisional} /></td>
                        <td className="px-3"><Rating v={r.assessment} prov={r.assessment_provisional} /></td>
                        <td className="px-3"><Rating v={r.composite} /></td>
                        <td className="px-3"><Rating v={r.casual} /></td>
                        <td className="px-3 text-xs font-bold text-primary-c" data-testid={`pdb-level-${r.user_id}`}>{r.level || "—"}</td>
                        <td className="px-3">{r.club || "—"}</td>
                        <td className="px-3">{r.location || "—"}</td>
                        <td className="px-3 mono">{r.skill || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}
